import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useDamageItems, DamageRecord } from '../../hooks/useDamageItems';
import { useProductAttributes } from '../../hooks/use-product-attributes';
import { getAdminId } from '../../utils/getAdminId';
import { format } from 'date-fns';
import {
  AlertTriangle,
  Clock,
  User,
  MapPin,
  Package,
  Image as ImageIcon,
  X,
  Search,
  Loader2,
} from 'lucide-react';

export const DamageHistory: React.FC = () => {
  const { user } = useAuth();
  const adminId = getAdminId(user);
  const { damageItems, loading, fetchDamageItems } = useDamageItems(adminId);
  const { colors, sizes } = useProductAttributes(adminId);

  const [searchQuery, setSearchQuery] = useState('');
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  useEffect(() => {
    if (adminId) {
      fetchDamageItems();
    }
  }, [adminId, fetchDamageItems]);

  const getColorName = (colorId?: string) => colors.find(c => c.id === colorId)?.name || '';
  const getColorHex = (colorId?: string) => colors.find(c => c.id === colorId)?.hex_code || '';
  const getSizeName = (sizeId?: string) => sizes.find(s => s.id === sizeId)?.name || '';

  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return damageItems;
    const q = searchQuery.toLowerCase();
    return damageItems.filter(item =>
      item.product_name.toLowerCase().includes(q) ||
      item.location_name.toLowerCase().includes(q) ||
      item.reported_by_name.toLowerCase().includes(q) ||
      item.description.toLowerCase().includes(q)
    );
  }, [damageItems, searchQuery]);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
      <div className="p-5 border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-red-100 dark:bg-red-900/30 rounded-xl">
              <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Damage History</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {damageItems.length} recorded damage {damageItems.length === 1 ? 'report' : 'reports'}
              </p>
            </div>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search damage records..."
              className="pl-9 pr-3 py-2 w-full sm:w-64 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm placeholder-gray-400 dark:placeholder-gray-500 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 transition-all"
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 text-red-500 animate-spin" />
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 px-4">
          <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-4">
            <Package className="h-8 w-8 text-gray-400 dark:text-gray-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
            {searchQuery ? 'No matching records' : 'No damage reports yet'}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center max-w-sm">
            {searchQuery
              ? 'Try adjusting your search terms.'
              : 'Damage reports will appear here once items are recorded.'}
          </p>
        </div>
      ) : (
        <>
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-700/50 bg-gray-50/50 dark:bg-gray-800/50">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date / Time</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Product</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Qty</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Color / Size</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Location</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Description</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Photo</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Reported By</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                {filteredItems.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="h-3.5 w-3.5 text-gray-400" />
                        <div>
                          <div className="text-gray-900 dark:text-gray-100">{format(new Date(item.created_at), 'dd/MM/yyyy')}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">{format(new Date(item.created_at), 'HH:mm')}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100 max-w-[200px] truncate">
                        {item.product_name}
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-center">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-bold bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
                        -{item.quantity}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex flex-wrap gap-1">
                        {getColorName(item.color_id) && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                            {getColorHex(item.color_id) && (
                              <span
                                className="w-2 h-2 rounded-full border border-amber-300"
                                style={{ backgroundColor: getColorHex(item.color_id) }}
                              />
                            )}
                            {getColorName(item.color_id)}
                          </span>
                        )}
                        {getSizeName(item.size_id) && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                            {getSizeName(item.size_id)}
                          </span>
                        )}
                        {!getColorName(item.color_id) && !getSizeName(item.size_id) && (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-1.5 text-sm">
                        <MapPin className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{item.location_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <p className="text-sm text-gray-600 dark:text-gray-400 max-w-[250px] truncate" title={item.description}>
                        {item.description}
                      </p>
                    </td>
                    <td className="px-4 py-3.5 text-center">
                      {item.image_url ? (
                        <button
                          onClick={() => setPreviewImage(item.image_url!)}
                          className="inline-flex items-center justify-center w-10 h-10 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600 hover:ring-2 hover:ring-teal-500/30 transition-all"
                        >
                          <img
                            src={item.image_url}
                            alt="Damage"
                            crossOrigin="anonymous"
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ) : (
                        <span className="text-gray-300 dark:text-gray-600">
                          <ImageIcon className="h-4 w-4 mx-auto" />
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-1.5 text-sm">
                        <User className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300 truncate max-w-[120px]">{item.reported_by_name}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="lg:hidden divide-y divide-gray-200 dark:divide-gray-700">
            {filteredItems.map((item) => (
              <div key={item.id} className="p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 dark:text-gray-100 truncate">{item.product_name}</div>
                    <div className="flex items-center gap-2 mt-1 text-xs text-gray-500 dark:text-gray-400">
                      <Clock className="h-3 w-3" />
                      {format(new Date(item.created_at), 'dd/MM/yyyy HH:mm')}
                    </div>
                  </div>
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-bold bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
                    -{item.quantity}
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                    <MapPin className="h-3 w-3" />
                    {item.location_name}
                  </span>
                  {getColorName(item.color_id) && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                      {getColorHex(item.color_id) && (
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: getColorHex(item.color_id) }} />
                      )}
                      {getColorName(item.color_id)}
                    </span>
                  )}
                  {getSizeName(item.size_id) && (
                    <span className="inline-flex items-center px-2 py-1 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                      {getSizeName(item.size_id)}
                    </span>
                  )}
                </div>

                <p className="text-sm text-gray-600 dark:text-gray-400">{item.description}</p>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                    <User className="h-3 w-3" />
                    {item.reported_by_name}
                  </div>
                  {item.image_url && (
                    <button
                      onClick={() => setPreviewImage(item.image_url!)}
                      className="inline-flex items-center gap-1 text-xs text-teal-600 dark:text-teal-400 hover:underline"
                    >
                      <ImageIcon className="h-3 w-3" />
                      View photo
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {previewImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative max-w-3xl max-h-[80vh] w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute -top-3 -right-3 p-2 bg-white dark:bg-gray-800 rounded-full shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors z-10"
            >
              <X className="h-5 w-5 text-gray-700 dark:text-gray-300" />
            </button>
            <img
              src={previewImage}
              alt="Damage photo"
              crossOrigin="anonymous"
              className="w-full h-auto max-h-[80vh] object-contain rounded-2xl shadow-2xl bg-white dark:bg-gray-800"
            />
          </div>
        </div>
      )}
    </div>
  );
};
